import { CaptchaConfig } from '../types'
import { apiService } from '../../../services/api'

/**
 * Frontend CAPTCHA Service
 * Handles communication with backend CAPTCHA endpoints
 */

const CAPTCHA_CONFIG_CACHE_KEY = 'captcha_config'
const CAPTCHA_CONFIG_CACHE_DURATION = 30 * 1000 // 30 seconds (reduced for testing)

/**
 * Get CAPTCHA configuration from backend or cache
 */
export const getCaptchaConfig = async (): Promise<CaptchaConfig> => {
  try {
    // Check cache first
    const cached = getCachedConfig()
    if (cached) {
      return cached
    }

    // Fetch CAPTCHA settings from public endpoint
    const { apiService } = await import('../../../services/api')
    const response = (await apiService.get('/api/admin-settings/captcha/config')) as any

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch CAPTCHA config')
    }

    const config: CaptchaConfig = response.data

    // Auto-disable CAPTCHA if siteKey is missing (graceful degradation)
    if (config.enabled && (!config.siteKey || config.siteKey.trim() === '')) {
      console.warn('⚠️ CAPTCHA enabled but siteKey is missing - auto-disabling CAPTCHA')
      config.enabled = false
    }

    // Cache the configuration
    setCachedConfig(config)

    console.log('✅ CAPTCHA config loaded from public endpoint:', {
      enabled: config.enabled,
      provider: config.provider,
      hasSiteKey: !!config.siteKey
    })
    return config
  } catch (error: any) {
    console.warn('Failed to fetch CAPTCHA config from public endpoint:', error.message)

    // Return safe defaults on error
    return getDefaultConfig()
  }
}

/**
 * Clear CAPTCHA configuration cache (for testing/admin purposes)
 */
export const clearCaptchaCache = (): void => {
  try {
    localStorage.removeItem(CAPTCHA_CONFIG_CACHE_KEY)
    console.log('🗑️ CAPTCHA cache cleared')
  } catch (error) {
    console.warn('Failed to clear CAPTCHA cache:', error)
  }
}

/**
 * Force refresh CAPTCHA configuration (bypass cache)
 */
export const refreshCaptchaConfig = async (): Promise<CaptchaConfig> => {
  try {
    // Clear cache first
    clearCaptchaCache()

    // Fetch fresh config
    const config = await getCaptchaConfig()
    console.log('🔄 CAPTCHA config refreshed:', config)
    return config
  } catch (error) {
    console.warn('Failed to refresh CAPTCHA config:', error)
    return getDefaultConfig()
  }
}

/**
 * Verify CAPTCHA token with backend
 */
export const verifyCaptcha = async (
  token: string,
  action?: string
): Promise<{ success: boolean; score?: number; error?: string }> => {
  try {
    const response = (await apiService.post('/captcha/verify', {
      token,
      action: action || 'form_submit'
    })) as any

    if (response.success) {
      return {
        success: true,
        score: response.data?.score
      }
    } else {
      return {
        success: false,
        error: response.message || 'CAPTCHA verification failed'
      }
    }
  } catch (error: any) {
    console.error('CAPTCHA verification error:', error.message)
    return {
      success: false,
      error: error.message || 'CAPTCHA service unavailable'
    }
  }
}

/**
 * Get CAPTCHA statistics
 */
export const getCaptchaStats = async () => {
  try {
    const response = (await apiService.get('/captcha/stats')) as any
    return response.success ? response.data : null
  } catch (error: any) {
    console.warn('Failed to fetch CAPTCHA stats:', error?.message || 'Unknown error')
    return null
  }
}

/**
 * Report CAPTCHA metrics (for analytics)
 */
export const reportCaptchaMetrics = async (metrics: {
  formType: string
  success: boolean
  responseTime: number
  error?: string
}) => {
  try {
    await apiService.post('/captcha/metrics', metrics)
  } catch (error: any) {
    // Silently fail - metrics are not critical
    console.debug('Failed to report CAPTCHA metrics:', error?.message || 'Unknown error')
  }
}

/**
 * Cache management functions
 */
const getCachedConfig = (): CaptchaConfig | null => {
  try {
    const cached = localStorage.getItem(CAPTCHA_CONFIG_CACHE_KEY)
    if (!cached) return null

    const { config, timestamp } = JSON.parse(cached)

    // Check if cache is still valid
    if (Date.now() - timestamp > CAPTCHA_CONFIG_CACHE_DURATION) {
      localStorage.removeItem(CAPTCHA_CONFIG_CACHE_KEY)
      return null
    }

    return config
  } catch (error: any) {
    // Clear corrupted cache
    localStorage.removeItem(CAPTCHA_CONFIG_CACHE_KEY)
    return null
  }
}

const setCachedConfig = (config: CaptchaConfig): void => {
  try {
    const cacheData = {
      config,
      timestamp: Date.now()
    }
    localStorage.setItem(CAPTCHA_CONFIG_CACHE_KEY, JSON.stringify(cacheData))
  } catch (error: any) {
    // Silently fail if localStorage is not available
    console.debug('Failed to cache CAPTCHA config:', error?.message || 'Unknown error')
  }
}

/**
 * Default CAPTCHA configuration (safe fallback)
 */
const getDefaultConfig = (): CaptchaConfig => ({
  enabled: false,
  provider: 'recaptcha-v3',
  siteKey: '',
  theme: 'light',
  size: 'normal',
  badge: 'bottomright'
})

export {
  CAPTCHA_CONFIG_CACHE_KEY,
  CAPTCHA_CONFIG_CACHE_DURATION
}
